import { Controller } from '@hotwired/stimulus'

const DISTANCE = '2rem'
const LEAVE = { duration: 260, easing: 'ease-in' }
const ENTER = {
  delay: 180, duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  fill: 'backwards'
}

// Slides are stacked in one grid cell and swapped in place. Moving
// forward, the current card leaves to the left and the next arrives from
// the right; moving back reverses both, like a track sliding past.
// The entry is delayed so the two cards barely overlap.
//
// Autoplay is the active dot's progress animation: when it finishes, the
// next slide shows. It pauses while hovered, focused or off screen, and
// is skipped entirely under reduced motion.
export default class extends Controller {
  static targets = ['slide', 'dot', 'progress']
  static values = { interval: { type: Number, default: 10000 } }

  connect() {
    this.hovered = false
    this.focused = false
    this.visible = false
    this.reducedMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this.observer = new IntersectionObserver(
      this.#handleIntersect.bind(this), { threshold: 0.4 }
    )
    this.observer.observe(this.element)
    this.#startProgress(this.#activeIndex())
  }

  disconnect() {
    this.observer.disconnect()
    this.progress?.cancel()
  }

  go(event) {
    const to = Number(event.currentTarget.dataset.index)
    if (to !== this.#activeIndex()) this.#show(to)
  }

  hold({ type }) {
    this.#setHold(type, true)
  }

  release({ type }) {
    this.#setHold(type, false)
  }

  #handleIntersect(entries) {
    entries.forEach(entry => {
      this.visible = entry.isIntersecting
      this.#syncProgress()
    })
  }

  // Hover and focus pause separately, so leaving with the pointer while a
  // dot still has keyboard focus does not resume.
  #setHold(type, active) {
    if (type.startsWith('mouse')) {
      this.hovered = active
    } else {
      this.focused = active
    }

    this.#syncProgress()
  }

  #activeIndex() {
    return this.slideTargets.findIndex(s => s.hasAttribute('data-active'))
  }

  #show(to) {
    const from = this.#activeIndex()
    this.#activate(to)
    this.#animate(from, to)
    this.#startProgress(to)
  }

  #activate(index) {
    this.slideTargets.forEach((slide, i) => {
      const active = i === index
      slide.toggleAttribute('data-active', active)
      slide.ariaHidden = active ? null : 'true'
    })

    this.dotTargets.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true')
      } else {
        dot.removeAttribute('aria-current')
      }
    })
  }

  #animate(from, to) {
    if (this.reducedMotion) return

    const forward = to > from
    const leaving = this.slideTargets[from]
    const entering = this.slideTargets[to]

    this.slideTargets.forEach(s => s.getAnimations().forEach(a => a.cancel()))

    leaving.animate([
      { opacity: 1, translate: '0px' },
      { opacity: 0, translate: forward ? `-${DISTANCE}` : DISTANCE }
    ], LEAVE)

    entering.animate([
      { opacity: 0, translate: forward ? DISTANCE : `-${DISTANCE}` },
      { opacity: 1, translate: '0px' }
    ], ENTER)
  }

  #startProgress(index) {
    this.progress?.cancel()
    if (this.reducedMotion) return

    // The fill is a full-size pill that slides in behind the clipped shell.
    this.progress = this.progressTargets[index].animate(
      [{ translate: '-100% 0' }, { translate: '0 0' }],
      { duration: this.intervalValue, easing: 'linear', fill: 'forwards' }
    )
    this.progress.onfinish = () => {
      this.#show((index + 1) % this.slideTargets.length)
    }
    this.#syncProgress()
  }

  #syncProgress() {
    if (!this.progress) return

    if (this.hovered || this.focused || !this.visible) {
      this.progress.pause()
    } else {
      this.progress.play()
    }
  }
}
